import { ChangeEvent, FC, FocusEvent, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { REGEX } from '@belong/constants';
import { applyDictionaryToString, AUTOCOMPLETE_TYPE } from '@belong/contentful';
import { IDictionary, IOnInputError, ITestable, TError, TMediaMap, TWidths, ValidationMessage } from '@belong/types';
import { validationFunctions } from '@belong/validation';
import { Copy } from '../../styles/Typography';
import { Error } from '../Error';
import { InputWrapper } from '../InputWrapper';
import { Label } from '../Label';
import { PasswordInput } from '../PasswordInput/PasswordInput';
import { IUseForm } from '../useForm';
import CONTENT from './content.json';
import type { ITextInput } from '../TextInput';

export interface IGenericPasswordInput extends Omit<ITextInput, 'autocomplete' | 'name' | 'required'>, ITestable {
  name: string;
  label?: string;
  showLabel?: string;
  hideLabel?: string;
  description?: string;
  dictionary?: IDictionary;
  width?: TWidths | TMediaMap;
  autoComplete: AUTOCOMPLETE_TYPE;
  errorMessages?: ValidationMessage<'invalid' | 'required'>;
  initialValue?: string;
  hasFormError: boolean;
  manageForm: IUseForm['manageForm'];
  onInputError?: IOnInputError;
}

const getValidationSchema = ({
  autoComplete,
  errorMessages
}: Pick<IGenericPasswordInput, 'autoComplete' | 'errorMessages'>) => {
  // We only want to validate the format if the password is a new password, not the current password such as
  // on the login form
  return Yup.string()
    .trim()
    .default('')
    .test('required', errorMessages?.required ?? CONTENT.errorMessages.required, inputValue =>
      validationFunctions.required({ value: inputValue! })
    )
    .when('autoComplete', {
      is: () => autoComplete === AUTOCOMPLETE_TYPE.NEW_PASSWORD,
      then: (schema: Yup.StringSchema) => {
        return schema.test('format', errorMessages?.invalid ?? CONTENT.errorMessages.invalid, inputValue =>
          validationFunctions.validFormat({ pattern: REGEX.PASSWORD, value: inputValue! })
        );
      }
    });
};

/**
 * A generic password input field. It validates required and a password regex. It handles form errors when implemented with the useForm hook.
 * Additionally it adds a show/hide button to change the visibility of the password input
 */
export const GenericPasswordInput: FC<IGenericPasswordInput> = ({
  name,
  label = CONTENT.label,
  description = CONTENT.description,
  showLabel = CONTENT.showLabel,
  hideLabel = CONTENT.hideLabel,
  dictionary = {},
  width,
  initialValue,
  hasFormError,
  manageForm,
  onInputError,
  autoComplete,
  errorMessages,
  'data-testid': testid = name,
  ...extraProps
}) => {
  const [error, setError] = useState<TError>({ hasError: hasFormError, message: '' });
  const [value, setValue] = useState<string>(initialValue ?? '');
  const idInput = `${testid}Input`;
  const idError = `${testid}Error`;
  const idLabel = `${testid}Label`;
  const idDescription = `${testid}Description`;
  const { hasError } = error;

  const validateValue = async (inputValue: string | null, forceValidate?: boolean): Promise<boolean> => {
    let isValid = true;
    const valueToVerify = inputValue || '';

    await getValidationSchema({ autoComplete, errorMessages })
      .validate(valueToVerify)
      .then(() => {
        setError({ hasError: false, message: '' });
        manageForm.removeInputError(name);
      })
      .catch(({ message }) => {
        manageForm.addInputError(name);
        isValid = false;
        if (error.hasError || forceValidate) {
          setError({ hasError: true, message });
        }
      });

    return isValid;
  };

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue);
      validateValue(initialValue, true);
    }
  }, [initialValue]);

  useEffect(() => {
    manageForm.addInputValidator(name, validateValue);

    return () => {
      manageForm.removeInputValidator(name);
    };
  }, [manageForm, name]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setValue(inputValue);
    validateValue(inputValue);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setValue(inputValue);
    validateValue(inputValue, true);
  };

  useEffect(() => {
    if (onInputError && error.hasError && error.message) {
      onInputError(name, applyDictionaryToString(dictionary, error.message));
    }
  }, [onInputError, name, error]);

  useEffect(() => {
    if (hasFormError && value.length === 0) {
      setError({ hasError: true, message: CONTENT.errorMessages.required });
      manageForm.addInputError(name);
    }

    return () => {
      manageForm.removeInputError(name);
    };
  }, [hasFormError]);

  return (
    <InputWrapper data-testid={testid}>
      <Label id={idLabel} htmlFor={idInput}>
        {applyDictionaryToString(dictionary, label)}
      </Label>

      {description && <Copy id={idDescription}>{applyDictionaryToString(dictionary, description)}</Copy>}

      <PasswordInput
        name={name}
        value={value}
        width={width}
        onChange={handleChange}
        onBlur={handleBlur}
        required
        isError={hasError}
        aria-invalid={hasError}
        aria-describedby={`${hasError ? idError : ''} ${description ? idDescription : ''}`.trim()}
        autoComplete={autoComplete}
        data-testid={`${testid}-input`}
        showLabel={showLabel}
        hideLabel={hideLabel}
        {...extraProps}
        id={idInput}
      />

      {hasError && <Error idError={idError}>{applyDictionaryToString(dictionary, error.message)}</Error>}
    </InputWrapper>
  );
};

GenericPasswordInput.displayName = 'GenericPasswordInput';
